/**
 * The composed router. No later plan modifies this file.
 *
 * A feature area is discovered by its path, never by name: the glob below
 * matches `routes.ts` in any directory one level up from this one.
 *
 * Vite resolves that at build time, so registering the control plane, the ops
 * plane or the tenant self-serve area is the single act of creating
 * `src/app/<area>/routes.ts` with a `bundle` export. A named import list here
 * would be one file that every route task in three plans has to edit, which is
 * exactly what this project's rule against a task declaring `Modify` on another
 * plan's file forbids; discovering by path makes the dependency disappear
 * rather than scheduling around it.
 *
 * The three auth route components are discovered the same way, for a narrower
 * reason: they were another task's files, running concurrently with this one.
 * All three have landed, so `requireComponent` now throws when one is missing
 * rather than substituting a placeholder -- see its doc comment for why the
 * placeholder became the wrong answer once the files existed.
 */

import { createElement } from 'react';
import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import type { AnyRoute } from '@tanstack/react-router';
import type { RouterHistory } from '@tanstack/history';

import { AppShell } from '../shell/AppShell';
import { bundlesFrom, guarded, requireComponent, sortByOrder } from './compose';
import { APP_BASEPATH, AUTH_ROUTE_PATHS, HOME_PATH } from './paths';
import type { RouteBundle } from './contract';
import { Home, NotFound, Root } from './root';

const areaModules = import.meta.glob<Record<string, unknown>>('../*/routes.ts', { eager: true });

const authModules = import.meta.glob<Record<string, unknown>>(
  ['./sign-in.tsx', './sign-up.tsx', './reset-password.tsx'],
  { eager: true },
);

export { AUTH_ROUTE_PATHS, HOME_PATH } from './paths';

export interface AppRouterOptions {
  /** A memory history, in a test. Omitted in the browser, where the router
   *  creates a browser history of its own. */
  history?: RouterHistory;
  /** Extra bundles, mounted exactly as a discovered one is -- same guard, same
   *  nav. A test uses this to mount a synthetic gated route rather than
   *  borrowing one of the control plane's, which would pass by not existing. */
  bundles?: RouteBundle[];
}

export function createAppRouter(options: AppRouterOptions = {}) {
  const bundles = [...bundlesFrom(areaModules), ...(options.bundles ?? [])];
  // Concatenated in whatever order `bundlesFrom` discovered the areas, then
  // sorted by each entry's own `order` -- the frame's arrangement is a
  // property of the entry, not of which area or module path produced it.
  const nav = sortByOrder(bundles.flatMap((bundle) => bundle.nav));
  const campaignNav = sortByOrder(bundles.flatMap((bundle) => bundle.campaignNav ?? []));

  const rootRoute = createRootRoute({ component: Root, notFoundComponent: NotFound });

  const authRoutes: AnyRoute[] = [
    createRoute({
      getParentRoute: () => rootRoute,
      path: AUTH_ROUTE_PATHS.signIn,
      component: requireComponent(authModules, './sign-in.tsx', 'SignIn'),
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: AUTH_ROUTE_PATHS.signUp,
      component: requireComponent(authModules, './sign-up.tsx', 'SignUp'),
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: AUTH_ROUTE_PATHS.resetPassword,
      component: requireComponent(authModules, './reset-password.tsx', 'ResetPassword'),
    }),
  ];

  // Pathless: it contributes no URL segment, it contributes the chrome. Every
  // route below it is inside the shell; the auth routes above it are not.
  const shellRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'shell',
    component: () => createElement(AppShell, { nav, campaignNav }),
  });

  // Public: parented to `rootRoute` beside the auth routes, same species as
  // `/sign-in` -- a URL a signed-out stranger is supposed to open, not a
  // page that assumes one. See `RouteModule.outsideShell`'s doc comment.
  const outsideShellRoutes: AnyRoute[] = bundles.flatMap((bundle) =>
    bundle.routes
      .filter((route) => route.outsideShell)
      .map((route) => {
        if (route.requires !== undefined) {
          throw new Error(
            `${route.path} sets both \`outsideShell\` and \`requires\` -- a public route has no capability to check`,
          );
        }
        return createRoute({
          getParentRoute: () => rootRoute,
          path: route.path,
          component: () => route.element,
        });
      }),
  );

  const featureRoutes: AnyRoute[] = bundles.flatMap((bundle) =>
    bundle.routes
      .filter((route) => !route.outsideShell)
      .map((route) =>
        createRoute({
          getParentRoute: () => shellRoute,
          path: route.path,
          component: () => guarded(route),
        }),
      ),
  );

  const redirectRoutes: AnyRoute[] = bundles.flatMap((bundle) =>
    (bundle.redirects ?? []).map((entry) =>
      createRoute({
        getParentRoute: () => shellRoute,
        path: entry.from,
        beforeLoad: ({ params }) => {
          throw redirect({ to: entry.to, params, search: entry.search, replace: true });
        },
      }),
    ),
  );

  // Parented beside the auth and outside-shell routes rather than under
  // `shellRoute`: `Home`'s no-organisation branch draws the same bare-page
  // frame they do (no rail, no top bar, no account footer), and its other
  // branches render nothing while they redirect, so nesting it under the
  // shell only doubled the `<main>` landmark and added the shell's own
  // header height to the page's.
  const homeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: HOME_PATH,
    component: Home,
  });

  const routeTree = rootRoute.addChildren([
    ...authRoutes,
    ...outsideShellRoutes,
    homeRoute,
    shellRoute.addChildren([...featureRoutes, ...redirectRoutes]),
  ]);

  // `basepath` is the one place the application's mount point is written
  // down; every route literal above is relative to it. See `APP_BASEPATH`.
  return createRouter({
    routeTree,
    history: options.history,
    basepath: APP_BASEPATH,
    // Back from a post lands where the reader was in the list.
    scrollRestoration: true,
  });
}

export const router = createAppRouter();
